<template>
  <div class="call-centre">
    <div class="header">
      <div>
        <div class="input-field">
          <div class="dropdown" :class="{ 'is-active': manager.isActive }">
            <div class="dropdown-trigger">
              <input
                class="input"
                type="text"
                placeholder="Zoek adres"
                :value="manager.query"
                @input="manager.setQuery"
                @blur="() => (isActive = false)"
                @click="() => (isActive = true)"
              />
            </div>
            <div class="dropdown-menu" id="dropdown-menu" role="menu">
              <div v-if="manager.isAddressesLoading" class="dropdown-content">
                <span class="dropdown-item">Laden...</span>
              </div>
              <div v-else-if="manager.addresses.length > 0" class="dropdown-content">
                <a
                  v-for="address in manager.addresses"
                  :key="address"
                  class="dropdown-item"
                  @click="() => manager.setSelectedAddress(address)"
                >
                  {{ address }}
                </a>
              </div>
              <div v-else-if="manager.query.length > 2" class="dropdown-content">
                <span class="dropdown-item">Geen resultaten</span>
              </div>
            </div>
          </div>
        </div>
        <p class="ml-3 p-2">Zoek hierboven een adres om de gegevens te zien</p>
      </div>
      <div v-if="selectedAddress && !manager.isAddressLoading" class="title">
        <span class="address"
          ><b>{{ address }}</b
          ><household-type-tag :address="manager.fullAddress"
        /></span>
        <span
          ><p>
            <span v-if="selectedAddress.woco"> {{ selectedAddress.woco }}, </span>
            <span v-if="selectedProjectGroups.length">
              {{ selectedProjectGroups.join(', ') }}
            </span>
          </p></span
        >
        <!-- One badge per project; with exactly 1 project the view stays as before -->
        <div v-if="clientProjects.length > 1" class="tags mt-1 mb-0">
          <span
            v-for="p in clientProjects"
            :key="p.name"
            class="tag is-medium"
            :class="p.id === manager.selectedProjectId ? 'is-info' : 'is-white'"
            style="cursor: pointer; border: 1px solid #dbdbdb"
            @click="manager.setSelectedProject(p.id)"
          >
            {{ p.name }}&nbsp;·&nbsp;{{ productTypeLabel(p) }}
            <span class="tag ml-2" :class="p.status === 'active' ? 'is-success' : 'is-light'">
              {{ p.status === 'active' ? 'actief' : 'afgerond' }}
            </span>
          </span>
        </div>
      </div>
      <div class="header-actions">
        <monitoring-links
          v-if="selectedAddress && !manager.isAddressLoading && selectedAddress.address"
          buttons
          :postal-code="selectedAddress.address.postal_code"
          :house-number="selectedAddress.address.house_number"
        />
        <dropdown-slotted
          v-if="selectedAddress && !manager.isAddressLoading"
          align="right"
          min-width="14rem"
        >
          <template v-slot:trigger>
            <button
              class="button is-info"
              :class="{ 'is-loading': manager.isEleenaSyncing || manager.isMarkingOutOfService }"
              title="Meer acties"
            >
              <i class="fas fa-ellipsis-h"></i>
            </button>
          </template>
          <template v-slot:default="{ close }">
          <a
            v-if="manager.ktoSurveyUrl"
            class="dropdown-item"
            :href="manager.ktoSurveyUrl"
            target="_blank"
            rel="noopener"
            @click="close()"
          >
            KTO-enquête openen
          </a>
          <span
            v-else
            class="dropdown-item has-text-grey-light"
            style="cursor: not-allowed"
            title="Geen KTO-enquête voor dit adres of project"
          >
            KTO-enquête openen
          </span>
          <hr class="dropdown-divider" />
            <a
              class="dropdown-item"
              :title="eleenaSyncTooltip"
              @click="() => { close(); manager.syncEleena() }"
            >
              Eleena synchroniseren
            </a>
            <a
              v-if="!isOutOfService"
              class="dropdown-item"
              @click="() => { close(); showOutOfServiceModal = true }"
            >
              Uit dienst zetten
            </a>
            <span
              v-else
              class="dropdown-item has-text-grey-light"
              style="cursor: not-allowed"
              title="Dit adres staat al uit dienst"
            >
              Uit dienst zetten
            </span>
          </template>
        </dropdown-slotted>
      </div>
    </div>

    <modal v-if="showOutOfServiceModal" :close="closeOutOfServiceModal">
      <div class="box" style="min-width: 400px">
        <h4 class="title is-5 mb-4">Uit dienst zetten</h4>
        <p class="mb-4">
          Is de meter voor dit adres al afgemeld? (Dit moet gebeuren voordat het adres uit dienst
          wordt gezet.)
        </p>
        <div class="field">
          <label class="label">Uit dienst datum</label>
          <input type="date" class="input" v-model="outOfServiceDate" />
        </div>
        <div class="buttons mt-4">
          <button class="button" @click="closeOutOfServiceModal">Annuleren</button>
          <button
            class="button is-primary"
            @click="confirmMarkOutOfService"
            :class="{ 'is-loading': manager.isMarkingOutOfService }"
            :disabled="!outOfServiceDate"
          >
            Bevestigen
          </button>
        </div>
      </div>
    </modal>
    <div class="columns">
      <div class="column is-narrow is-flex is-align-items-center">
        <dashboard :manager="manager" v-if="showDashboard" />
        <button @click="() => (showDashboard = !showDashboard)" class="button ml-1 p-2">
          <i class="fas fa-angle-right" v-if="!showDashboard"></i>
          <i class="fas fa-angle-left" v-else></i>
        </button>
      </div>
      <div class="column mt-5 ml-5" v-if="manager.isAddressLoading">Loading...</div>
      <div class="column mt-5 ml-5" v-else-if="manager.isInvalidAddress">
        Something went wrong. Please try again.
      </div>
      <div class="column" v-else-if="selectedAddress">
        <div class="actions">
          <div
            v-if="manager?.selectedAddress && manager.selectedAddress.out_of_service"
            class="notification is-warning mb-3"
          >
            <div class="is-flex is-justify-content-space-between is-align-items-center">
              <strong
                >Out of service since:
                {{ new Date(manager.selectedAddress.out_of_service).toLocaleDateString('en-GB') }}</strong
              >
              <button
                class="button is-small is-light"
                :class="{ 'is-loading': manager.isMarkingOutOfService }"
                @click="manager.toggleServiceStatus(null)"
              >
                Undo
              </button>
            </div>
          </div>
          <div v-if="manager.actions.length > 0" ref="scrollable" class="actions-list mb-3">
            <div
              v-for="action in manager.actions"
              :key="action.id"
              class="card m-2"
              :class="{
                inbound: action.is_inbound,
                outbound: !action.is_inbound,
                'related-action-resolved':
                  manager.selectedAction &&
                  action.related_action_id === manager.selectedAction.id &&
                  manager.selectedAction.is_resolved,
                'related-action-unresolved':
                  manager.selectedAction &&
                  action.related_action_id === manager.selectedAction.id &&
                  !manager.selectedAction.is_resolved,
                'selected-action-resolved':
                  manager.selectedAction &&
                  action.id === manager.selectedAction.id &&
                  action.is_resolved,
                'selected-action-unresolved':
                  manager.selectedAction &&
                  action.id === manager.selectedAction.id &&
                  !action.is_resolved,
              }"
            >
              <div class="card-content">
                <div class="content">
                  <span>
                    <b>{{ action.subject }}</b
                    >, <b>{{ action.type }}</b>
                    <span class="ml-1">
                      <i class="fas fa-arrow-right" v-if="action.is_inbound"></i>
                      <i class="fas fa-arrow-left" v-else></i>
                      {{ action.is_inbound ? 'IN' : ' OUT' }}, {{ action.context }},
                    </span>
                    <span>
                      <span
                        ><i>{{ action.created_by }}</i></span
                      >,
                      <span>{{ manager.formatDate(new Date(action.created_at)) }}</span>
                    </span>
                  </span>
                  <p>{{ action.value }}</p>
                </div>
              </div>
              <footer class="card-footer">
                <a class="card-footer-item" v-if="action.isLoading">Loading...</a>
                <a
                  v-else-if="action.has_follow_up && !action.is_resolved"
                  class="card-footer-item"
                  @click="() => resolve(action)"
                  >Resolve</a
                >
              </footer>
            </div>
          </div>
          <div v-else class="actions-list mb-3 ml-2">
            <p>No comments have been added to this address yet.</p>
          </div>
          <div class="form">
            <textarea
              v-model="manager.action.value"
              class="textarea"
              :placeholder="`${manager.action.type}...`"
              rows="2"
            ></textarea>
            <div class="options">
              <div class="dropdowns is-align-items-center">
                <dropdown
                  display="label"
                  class="mr-2"
                  :preselect="manager.action.context"
                  :items="contextDropdown"
                  @change="(item) => item.handler()"
                />
                <div class="labels mr-2">
                  <label class="ml-1 radio" style="display: flex; align-items: flex-end">
                    <input
                      type="radio"
                      @change="() => (manager.action.is_inbound = true)"
                      :checked="manager.action.is_inbound"
                    />
                    <span class="pl-1">Inbound</span>
                  </label>
                  <label class="ml-1 radio" style="display: flex; align-items: flex-end">
                    <input
                      type="radio"
                      @change="() => (manager.action.is_inbound = false)"
                      :checked="!manager.action.is_inbound"
                    />
                    <span class="pl-1">Outbound</span>
                  </label>
                </div>
                <dropdown
                  class="mr-2"
                  display="label"
                  width="145"
                  :preselect="manager.action.subject"
                  :items="subjectDropdown"
                  @change="(item) => item.handler()"
                />
                <dropdown
                  class="mr-2"
                  display="label"
                  :preselect="manager.action.type"
                  :items="typeDropdown"
                  @change="(item) => item.handler()"
                />
                <dropdown
                  class="mr-2"
                  display="label"
                  width="90"
                  :items="relatedActionDropdown"
                  @change="(item) => item.handler()"
                />

                <label class="mr-2 checkbox" style="display: flex; align-items: flex-end">
                  <span class="pr-1">Follow up</span>
                  <input
                    type="checkbox"
                    @change="() => (manager.action.has_follow_up = !manager.action.has_follow_up)"
                    :checked="manager.action.has_follow_up"
                  />
                </label>
              </div>
              <button
                :disabled="!manager.action.value"
                class="button is-info is-small"
                :class="{ 'is-loading': manager.action.isLoading }"
                @click="create"
              >
                Log
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="column mt-5 ml-5"></div>
      <div
        class="column"
        v-if="selectedAddress && !manager.isAddressLoading && !manager.isInvalidAddress"
      >
        <call-centre-status-tabs
          :statuses="statusTabs"
          :selected="manager.selectedStatus"
          :address="manager.fullAddress"
          :tasks="selectedAddress.tasks || {}"
          :location-id="locationId"
          @select="manager.setSelectedStatus"
        />
        <div class="box details">
          <p v-if="selectedAddress.groups" style="font-size: 1.1em; text-decoration: underline">
            <a target="_blank" :href="taskUrl">Go to tasks</a>
          </p>
          <!-- Compact status lines for the address's other (non-selected) projects -->
          <div v-if="otherProjectSummaries.length" class="mb-2">
            <div v-for="entry in otherProjectSummaries" :key="entry.name" style="font-size: 0.9em">
              <b>{{ entry.name }}</b
              >:
              <span v-for="(summary, taskType) in entry.tasks" :key="taskType" class="mr-2">
                {{ taskType }}: <i>{{ summary.status }}</i>
              </span>
            </div>
          </div>
          <client-data
            v-if="manager.selectedStatus === 'Client'"
            :client="this.manager.selectedAddress"
          />
          <design-data
            v-else-if="manager.selectedStatus === 'Design'"
            :client="this.manager.selectedAddress"
          />
          <sales-data
            v-else-if="manager.selectedStatus === 'Sales'"
            :client="this.manager.selectedAddress"
          />
          <installation-data
            v-else-if="manager.selectedStatus === 'Installation'"
            :client="this.manager.selectedAddress"
          />
          <eleena-data
            v-else-if="manager.selectedStatus === 'Eleena'"
            :client="this.manager.selectedAddress"
            :manager="this.manager"
          />
          <inspection-data
            v-else-if="manager.selectedStatus === 'Inspection'"
            :client="this.manager.selectedAddress"
          />
          <CallCentreTickets
            :address="this.manager.fullAddress"
            v-else-if="manager.selectedStatus === 'Tickets'"
            :client="this.manager.selectedAddress"
          />
          <call-centre-cases
            v-else-if="manager.selectedStatus === 'Cases'"
            :location-id="locationId"
          />
        </div>
      </div>
      <div class="column" v-else></div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import { Action, CallCentreScreenManager } from '@/logic/CallCentreScreen'
import {
  ClientData,
  DesignData,
  SalesData,
  InstallationData,
  EleenaData,
  Dashboard,
  InspectionData,
  CallCentreTickets,
} from '@/components/CallCentreScreen'
import CallCentreStatusTabs from '@/components/CallCentreScreen/CallCentreStatusTabs.vue'
import HouseholdTypeTag from '@/components/CallCentreScreen/HouseholdTypeTag.vue'
import CallCentreCases from '@/components/CallCentreScreen/CallCentreCases.vue'
import MonitoringLinks from '@/components/shared/MonitoringLinks.vue'
import { toLocationId } from '@/util/meterLinks'
import { events } from '@/util'
import { productTypeOptions } from '@/constants'

@Component({
  components: {
    ClientData,
    DesignData,
    SalesData,
    InstallationData,
    EleenaData,
    Dashboard,
    InspectionData,
    CallCentreTickets,
    CallCentreStatusTabs,
    HouseholdTypeTag,
    CallCentreCases,
    MonitoringLinks,
  },
})
export default class CallCentreView extends Vue {
  public manager = new CallCentreScreenManager(this.$router)

  // the complaints list: collapsed by default, the arrow button opens it
  public showDashboard = false
  public showOutOfServiceModal = false
  public outOfServiceDate = ''

  get taskUrl() {
    let { selectedStatus } = this.manager

    if (selectedStatus === 'Eleena') selectedStatus = 'Installation'
    if (selectedStatus === 'Client') selectedStatus = 'Design'
    // Tickets and Cases are no task types: open the design pane, as for Bewoner
    if (selectedStatus === 'Tickets' || selectedStatus === 'Cases') selectedStatus = 'Design'

    const queryParams = [
      `woco=${this.manager.selectedAddress?.woco}`,
      `project=${this.manager.selectedAddress?.project}`,
      // The board link must open a group of the selected project
      `group=${this.selectedProjectGroups[0] ?? this.manager.selectedAddress?.groups[0]}`,
      `panes=${selectedStatus}Pane`,
      `address=${this.manager.fullAddress}`,
      `detail=${selectedStatus.toLowerCase()}`,
      `search=${this.manager.fullAddress}`,
    ]
    return `/#/tasks?${queryParams.join('&')}`
  }

  get selectedAddress() {
    return this.manager.selectedAddress
  }

  get clientProjects() {
    return this.manager.clientProjects
  }

  get selectedProjectGroups(): Array<string> {
    const client = this.selectedAddress
    if (!client) return []

    // Groups of the SELECTED project; groups[0] stays the fallback for
    // responses without the group_projects map (older groups service)
    const groupProjects = client.group_projects || {}
    const selectedName = this.manager.selectedProject?.name ?? client.project
    const matching = (client.groups || []).filter((group) => groupProjects[group] === selectedName)
    return matching.length ? matching : (client.groups || []).slice(0, 1)
  }

  get otherProjectSummaries() {
    const summaries = this.selectedAddress?.tasks_by_project || {}
    return Object.entries(summaries).map(([name, entry]) => ({ name, ...entry }))
  }

  get eleenaSyncTooltip() {
    const project = this.manager.selectedProject
    return project
      ? `Synchroniseert Eleena voor het geselecteerde project: ${project.name}`
      : 'Synchroniseert Eleena voor het project van dit adres'
  }

  public productTypeLabel(project: { product_type: string | null; name: string }) {
    // Noly is routed on product_type, with the name check as fallback for
    // projects from before the product_type field existed
    if (this.manager.isNolyProject(project as any)) return 'Noly'
    const option = productTypeOptions.find((o) => o.value === project.product_type)
    return option ? option.label : project.product_type || 'PV'
  }

  get isOutOfService() {
    return this.selectedAddress && this.selectedAddress.out_of_service
  }

  get relatedActionDropdown() {
    const actions = this.manager.actions.filter((action) => action.has_follow_up)
    const dropdown = actions.map((action) => ({
      label: action.value.substring(0, 100),
      handler: () => {
        this.manager.action.related_action_id = action.id
      },
    }))
    dropdown.unshift({
      label: 'Related',
      handler: () => {
        this.manager.action.related_action_id = null
      },
    })
    return dropdown
  }

  get statusTabs() {
    return [...this.manager.possibleStatuses, 'Tickets', 'Cases']
  }

  // tickets location id (1234AB12-A) of the address, for its cases
  get locationId() {
    const address = this.selectedAddress?.address
    return address ? toLocationId(address.postal_code, address.house_number) : null
  }

  get typeDropdown() {
    const types = this.manager.action.possibleTypes
    const dropdown = types.map((value) => ({
      label: value,
      handler: () => {
        if (value === 'Complaint') {
          this.manager.action.has_follow_up = true
          this.manager.action.is_inbound = true
        }
        this.manager.action.type = value
      },
    }))
    return dropdown
  }

  get subjectDropdown() {
    const types = this.manager.action.possibleSubjects
    const dropdown = types.map((value) => ({
      label: value,
      handler: () => {
        this.manager.action.subject = value
      },
    }))
    return dropdown
  }

  get contextDropdown() {
    const types = this.manager.action.possibleContext
    const dropdown = types.map((value) => ({
      label: value,
      handler: () => {
        this.manager.action.context = value
      },
    }))
    return dropdown
  }

  get address() {
    console.log(this.manager)
    if (!this.manager.selectedAddress || !this.manager.fullAddress) return ''
    return this.manager.fullAddress
  }

  public async create() {
    await this.manager.action.create()
    this.manager.setActions(this.manager.fullAddress)

    this.manager.action = new Action()
    this.manager.dashboard.setActions()
    this.manager.action.address = this.address
    this.manager.action.woco = this.manager.selectedAddress!.woco

    this.$nextTick(() => {
      ;(this.$refs.scrollable as HTMLElement).scrollTop = 0
    })
  }

  public async resolve(action: Action) {
    await action.resolve()

    this.manager.dashboard.setActions()
  }

  public closeOutOfServiceModal() {
    this.showOutOfServiceModal = false
    this.outOfServiceDate = ''
  }

  public async confirmMarkOutOfService() {
    const dateTime = new Date(this.outOfServiceDate).toISOString()
    await this.manager.toggleServiceStatus(dateTime)
    this.closeOutOfServiceModal()
  }

  protected onClientUpdate() {
    if (!this.manager.selectedAddress) return

    const { street, house_number, postal_code } = this.manager.selectedAddress!.address
    const address = this.manager.selectedAddress!.fullAddress
    this.manager.setSelectedAddress(address)
  }

  updated() {
    this.$nextTick(() => {
      if (!this.manager.actionWasSelected) return
      let offsetTop = 0
      const resolvedElements = document.getElementsByClassName('selected-action-resolved')
      const unresolvedElements = document.getElementsByClassName('selected-action-unresolved')
      if (resolvedElements.length === 0 && unresolvedElements.length === 0) return
      if (resolvedElements.length > 0) {
        const element = resolvedElements[0] as HTMLElement
        offsetTop = element.offsetTop
      }
      if (unresolvedElements.length > 0) {
        const element = unresolvedElements[0] as HTMLElement
        offsetTop = element.offsetTop
      }
      const scrollable = this.$refs.scrollable as HTMLElement
      if (scrollable) {
        scrollable.scrollTop = offsetTop - 173
        this.manager.actionWasSelected = false
      }
    })
  }

  beforeDestroy() {
    events.off('client-updated', this.onClientUpdate)
  }

  mounted() {
    events.on('client-updated', this.onClientUpdate)
  }
}
</script>

<style lang="scss">
.call-centre {
  font-size: 0.8em;
  padding: 1rem;

  .details {
    overflow-y: scroll;

    &::-webkit-scrollbar {
      width: 0.35rem;
      height: 0.35rem;
      color: #868686;
    }

    &::-webkit-scrollbar-track {
      -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
      box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
      box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
      border-radius: 10px;
      cursor: grab;
    }
  }

  .columns {
    height: 75vh;

    .column {
      height: 100%;
      padding: 0.3em;

      .box {
        height: 90%;
      }

      .actions {
        height: 100%;
        display: flex;
        flex-direction: column;

        .actions-list {
          overflow-y: scroll;
          flex-grow: 1;
          scroll-behavior: smooth;

          &::-webkit-scrollbar {
            width: 0.35rem;
            height: 0.35rem;
            color: #868686;
          }

          &::-webkit-scrollbar-track {
            -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
            box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
            border-radius: 10px;
          }

          &::-webkit-scrollbar-thumb {
            -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
            box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
            border-radius: 10px;
            cursor: grab;
          }

          .inbound {
            background-color: #edf6fa;
            .card-footer {
              border-top: 1px solid #b4d3e1;

              .card-footer-item {
                color: #4f8196;
              }
            }
          }

          .outbound {
            background-color: #eaf7e1;
            .card-footer {
              border-top: 1px solid #b0c99d;

              .card-footer-item {
                color: #5c7e43;
              }
            }
          }

          .related-action-resolved {
            border: 0.2em solid #7eea69;
            box-shadow: inset 0 0 0.5rem #7eea69;
          }

          .related-action-unresolved {
            border: 0.2em solid #fa6645;
            box-shadow: inset 0 0 0.5rem #fa6645;
          }

          .selected-action-resolved {
            color: #161616;
            background-color: #7eea69;
          }

          .selected-action-unresolved {
            color: #161616;
            background-color: #fa6645;
            .card-footer {
              border-top: 1px solid #540f00;

              .card-footer-item {
                color: #540f00;
              }
            }
          }
        }
      }
    }

    .card {
      .card-content {
        padding: 0.7rem;
        font-size: 0.9em;
        white-space: pre-line;
        user-select: text;

        .content {
          display: flex;
          flex-direction: column;

          p:not(:last-child) {
            margin-bottom: 0.2em;
          }
        }
      }

      .card-footer-item {
        padding: 0.3rem;
        font-size: 0.9em;
      }
    }

    .form {
      display: flex;
      flex-direction: column;

      .textarea {
        min-width: auto;
      }

      .options {
        margin-top: 1em;
        display: flex;
        flex-direction: row;

        .dropdowns {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          row-gap: 0.5em;
        }
      }
    }

    .selected-address {
      margin: 1rem;
      display: flex;

      & > * {
        width: 50%;
      }
    }

    .address-results {
      border: 1px solid black;
      padding: 0.7rem 0;
      width: 30rem;
      z-index: 30;

      .item {
        cursor: pointer;
        padding: 0 0.1rem;

        &:hover {
          background-color: lightblue;
        }
      }
    }
  }
}

.header {
  // search, address and actions on one line: everything starts at the top, the address line is as
  // high as the search field and the buttons (2.5rem)
  display: flex;
  align-items: flex-start;
  padding-top: 1rem;

  .title {
    margin-left: 2rem;
    margin-bottom: 0;
    font-size: 1rem;

    .address {
      font-size: 1.2rem;
      line-height: 2.5rem;
    }
  }

  .header-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .input-field {
    margin-left: 1rem;

    .dropdown {
      width: 25rem;

      .dropdown-content {
        overflow-y: scroll;
        width: 25rem;
        max-height: 30rem;

        .dropdown-item {
          font-size: 0.65rem;
          padding: 0.05rem 0.6rem;
        }

        &::-webkit-scrollbar {
          width: 0.35rem;
          height: 0.35rem;
          color: #868686;
        }

        &::-webkit-scrollbar-track {
          -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
          box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
          border-radius: 10px;
        }

        &::-webkit-scrollbar-thumb {
          -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
          box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
          border-radius: 10px;
          cursor: grab;
        }
      }

      .dropdown-trigger {
        width: 100%;
      }
    }
  }
}
</style>
